import { getActionHandler, useActionDispatcher, useApi } from "@judo/actions";
import type { Action } from "@judo/actions";
import {
	useDebouncedCallback,
	useApplication,
	useDataSelector,
	useDataStore,
	useNavigation,
	usePageContextOptional,
	useResolvedPageActionOverrides,
	useRuntimeConfigOptional,
} from "@judo/core";
import { useConfirmation, useNotifications } from "@judo/feedback";
import { useModelLabel } from "@judo/i18n";
import type { Button as ButtonElement, Column, Link, PageDefinition, RelationType } from "@judo/model-api";
import { getButtonTestId, getLinkTestId } from "@judo/test-ids";
import {
	Autocomplete,
	Box,
	ClickAwayListener,
	Grow,
	IconButton,
	MenuItem,
	MenuList,
	Paper,
	Popper,
	TextField,
	Tooltip,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useCallback, useMemo, useRef, useState } from "react";
import { LinkSelectorDialog } from "../dialogs/LinkSelectorDialog";
import { buildMaskString } from "../utils/build-mask-string";
import { getDestructiveConfirmationOptions } from "../utils/destructive-action-utils";
import { findActionByDefinition } from "../utils/find-action-by-definition";
import { getVisibleButtons } from "../utils/get-visible-buttons";
import { buildIconAdornment } from "../utils/icon-adornment";
import { IconRenderer } from "./IconRenderer";

export interface LinkRendererProps {
	element: Link;
	page?: PageDefinition;
	readOnly?: boolean;
}

type OptionRecord = Record<string, unknown>;

/**
 * Renderer for Link (autocomplete/relation) elements.
 *
 * Supports:
 * - Typeahead autocomplete with backend fetch (getRelationRange)
 * - Action buttons: View, Set (selector dialog), Unset, Create, Delete
 * - Selector dialog with DataGrid, filtering, pagination
 * - Read-only mode
 */
export function LinkRenderer({ element, page: pageProp, readOnly }: LinkRendererProps) {
	const testId = getLinkTestId(element);
	const { label } = useModelLabel(element);
	const startAdornment = buildIconAdornment(element.icon);

	// Contexts
	const pageContext = usePageContextOptional();
	const page = pageProp ?? pageContext?.pageDefinition;
	const navigation = useNavigation();
	const dataStore = useDataStore();
	const application = useApplication();
	const api = useApi();
	const notifications = useNotifications();
	const { confirm } = useConfirmation();
	const runtimeConfig = useRuntimeConfigOptional();

	const density = runtimeConfig?.config?.density ?? "compact";

	// Density-aware margin mapping for the link actions box.
	// Each density level needs different margins to align with the Autocomplete's OutlinedInput.
	const densityMargins = useMemo(() => {
		const mapping = {
			compact: { top: 1.33333334, bottom: 0.66666667 },
			comfortable: { top: 0, bottom: 0 },
			standard: { top: 1.63333332, bottom: 0.8 },
		};
		return mapping[density] ?? mapping.comfortable;
	}, [density]);

	// Local state
	const [options, setOptions] = useState<OptionRecord[]>([]);
	const [autocompleteLoading, setAutocompleteLoading] = useState(false);
	const [selectorOpen, setSelectorOpen] = useState(false);

	// Resolve the relation type from element.dataElement
	const relationType = useMemo(() => {
		if (
			element.dataElement &&
			typeof element.dataElement === "object" &&
			element.dataElement["@type"] === "RelationType"
		) {
			return element.dataElement as RelationType;
		}
		return undefined;
	}, [element.dataElement]);

	// Subscribe to the current relation value from the DataStore
	const relationValue = useDataSelector(
		useCallback(
			(state) => {
				const transferId = pageContext?.transferId;
				if (!transferId) return null;
				const transferState = state.transfers.get(transferId);
				if (!transferState?.data) return null;
				return (transferState.data[element.relationName] as OptionRecord) ?? null;
			},
			[pageContext?.transferId, element.relationName]
		)
	);

	// Subscribe to loading state
	const _isLoading = useDataSelector(
		useCallback(
			(state) => {
				const transferId = pageContext?.transferId;
				if (!transferId) return false;
				return state.transfers.get(transferId)?.isLoading ?? false;
			},
			[pageContext?.transferId]
		)
	);

	// Get current transfer data for action context
	const storedTransfer = useDataSelector(
		useCallback(
			(state) => {
				const transferId = pageContext?.transferId;
				return transferId ? state.transfers.get(transferId)?.data : undefined;
			},
			[pageContext?.transferId]
		)
	);

	// Build transfer for action context (with signedIdentifier)
	// Prefer storedTransfer when available — it contains the correct __identifier
	// and __signedIdentifier from the backend, plus all attributes needed for
	// range requests (e.g., LinkSelectorDialog sends the full owner in the body).
	const transfer = useMemo(() => {
		if (storedTransfer) {
			return storedTransfer;
		}
		const signedIdentifier = pageContext?.params?.signedIdentifier as string | undefined;
		if (signedIdentifier && pageContext?.transferId) {
			return {
				__identifier: pageContext.transferId,
				__signedIdentifier: signedIdentifier,
			};
		}
		return undefined;
	}, [storedTransfer, pageContext?.transferId, pageContext?.params]);

	// Effective readOnly: from prop, element, or relation
	const isReadOnly = readOnly ?? element.isReadOnly ?? relationType?.isReadOnly ?? false;

	// Get resolved page action overrides from customizations
	const actionOverrides = useResolvedPageActionOverrides();

	// Action dispatcher setup (following TableRenderer pattern)
	const dispatcherContext = useMemo(
		() =>
			({
				navigation,
				data: dataStore,
				registry: application,
				api,
				transfer,
				notifications,
				actionOverrides,
				additionalContext: {
					isEager: element.isEager,
					relationName: element.relationName,
					pageTransferId: pageContext?.transferId,
					// For eager links, pass parentTransferId so View/Create dialogs
					// get the eager context (matching the TableRenderer pattern)
					...(element.isEager && {
						parentTransferId: pageContext?.transferId,
					}),
				},
			}) as unknown as Parameters<typeof useActionDispatcher>[0],
		[
			navigation,
			dataStore,
			application,
			api,
			transfer,
			notifications,
			element.isEager,
			element.relationName,
			pageContext?.transferId,
			actionOverrides,
		]
	);

	const { dispatch } = useActionDispatcher(dispatcherContext);

	// Get display label from parts
	const getOptionLabel = useCallback(
		(option: OptionRecord): string => {
			if (!option) return "";
			return element.parts
				.map((p: Column) => option[p.name])
				.filter(Boolean)
				.join(" ");
		},
		[element.parts]
	);

	// Determine the first string attribute name on the target type for filtering
	const filterAttributeName = useMemo(() => {
		if (!relationType?.target?.attributes) return undefined;
		const stringAttr = relationType.target.attributes.find((attr) => attr.dataType?.["@type"] === "StringType");
		return stringAttr?.name;
	}, [relationType]);

	// Fetch relation range from backend
	const fetchRange = useCallback(
		async (filterValue?: string) => {
			if (!relationType || !api) return;

			setAutocompleteLoading(true);
			try {
				const queryCustomizer: Record<string, unknown> = {
					_seek: { limit: element.autoCompleteRows ?? 10 },
					_mask: buildMaskString({
						attributes: [...(relationType.target.attributes ?? []), ...(element.additionalMaskAttributes ?? [])],
						relations: element.additionalMaskRelations,
					}),
				};

				if (filterValue && filterAttributeName) {
					queryCustomizer[filterAttributeName] = [{ operator: "like", value: `%${filterValue}%` }];
				}

				// Use storedTransfer (backend data with correct __identifier UUID) rather than the
				// constructed transfer which may have the frontend store key as __identifier.
				const response = await api.getRelationRange(
					relationType,
					(storedTransfer ?? transfer) as any,
					queryCustomizer as any
				);
				setOptions(response.data as OptionRecord[]);
			} catch (error) {
				console.error("Failed to load autocomplete options:", error);
				setOptions([]);
			} finally {
				setAutocompleteLoading(false);
			}
		},
		[
			element.autoCompleteRows,
			element.additionalMaskAttributes,
			element.additionalMaskRelations,
			relationType,
			api,
			storedTransfer,
			transfer,
			filterAttributeName,
		]
	);

	// Fetch initial (unfiltered) range when dropdown opens
	const handleOpen = useCallback(() => {
		fetchRange();
	}, [fetchRange]);

	// Debounced fetch for autocomplete input typing
	const debouncedFetchRange = useDebouncedCallback((value: string) => {
		fetchRange(value);
	});

	// Handle autocomplete input change — fetch range with built-in debounce
	const handleInputChange = useCallback(
		(_event: unknown, value: string, reason: string) => {
			if (reason !== "input") return;

			if (!element.autocompleteRangeActionDefinition || !relationType) {
				return;
			}

			debouncedFetchRange(value);
		},
		[element.autocompleteRangeActionDefinition, relationType, debouncedFetchRange]
	);

	// Ensure the current relationValue is always available in the options list
	// so MUI Autocomplete can match value → options for display
	const mergedOptions = useMemo(() => {
		if (!relationValue) return options;
		const alreadyPresent = options.some((opt) => {
			// Prefer __identifier (stable UUID) for dedup since __signedIdentifier
			// may differ between DataStore value and fresh range response
			if (opt.__identifier && relationValue.__identifier) {
				return opt.__identifier === relationValue.__identifier;
			}
			if (opt.__signedIdentifier && relationValue.__signedIdentifier) {
				return opt.__signedIdentifier === relationValue.__signedIdentifier;
			}
			return opt === relationValue;
		});
		if (alreadyPresent) return options;
		return [relationValue, ...options];
	}, [options, relationValue]);

	// Resolve visible action buttons (moved before handleChange so unsetButton is available)
	const visibleButtons = useMemo(() => {
		const allButtons = element.actionButtonGroup?.buttons ?? [];
		return getVisibleButtons(allButtons, page?.actions);
	}, [element.actionButtonGroup, page?.actions]);

	// Categorize buttons by action type
	const buttonsByType = useMemo(() => {
		const result: Record<string, (typeof visibleButtons)[number]> = {};
		for (const button of visibleButtons) {
			const actionType = button.actionDefinition?.["@type"];
			if (actionType) {
				result[actionType] = button;
			}
		}
		return result;
	}, [visibleButtons]);

	// Cardinality check: isOptional defaults to true; when false the relation is required
	const isOptional = relationType?.isOptional !== false;

	// Resolve individual buttons from the model
	const viewButton = buttonsByType.OpenPageActionDefinition ?? buttonsByType.RowOpenPageActionDefinition ?? undefined;
	const setButton = !isReadOnly ? buttonsByType.OpenSetSelectorActionDefinition : undefined;
	const unsetButton = !isReadOnly && isOptional ? buttonsByType.UnsetActionDefinition : undefined;
	const createButton = !isReadOnly ? (buttonsByType.OpenCreateFormActionDefinition ?? undefined) : undefined;
	const deleteButton = isOptional ? buttonsByType.RowDeleteActionDefinition : undefined;

	// Handle autocomplete selection
	const handleChange = useCallback(
		async (_event: unknown, value: OptionRecord | null) => {
			// Clear button clicked → unset the relation
			if (!value) {
				if (element.isEager && pageContext?.transferId) {
					// Eager: just clear the DataStore field, no REST call
					dataStore.updateTransferField(pageContext.transferId, element.relationName, null);
				} else {
					// Lazy: dispatch the unset REST action (uses pre-resolved unsetButton)
					const unsetAction = unsetButton?.actionDefinition
						? findActionByDefinition(page!, unsetButton.actionDefinition)
						: undefined;

					if (unsetAction) {
						try {
							const handlerHook = getActionHandler(unsetAction as Action);
							if (handlerHook) {
								const handlerFn = handlerHook(unsetAction as Action);
								await handlerFn({
									navigation,
									data: dataStore,
									registry: application,
									api,
									transfer: relationValue ?? transfer,
									notifications,
									...dispatcherContext.additionalContext,
								} as any);
							}
						} catch (error) {
							console.error("Failed to unset autocomplete value:", error);
						}
					}
				}
				return;
			}

			const setAction = element.autocompleteSetActionDefinition
				? findActionByDefinition(page!, element.autocompleteSetActionDefinition)
				: undefined;

			if (setAction) {
				try {
					await dispatch(setAction as Action, { selectedItem: value });

					// For eager relations, update the parent transfer field to trigger edit mode
					if (element.isEager && pageContext?.transferId) {
						dataStore.updateTransferField(pageContext.transferId, element.relationName, value);
					}
				} catch (error) {
					console.error("Failed to set autocomplete value:", error);
				}
			}
		},
		[
			element.autocompleteSetActionDefinition,
			unsetButton,
			element.isEager,
			element.relationName,
			page,
			dispatch,
			dataStore,
			pageContext?.transferId,
			navigation,
			application,
			api,
			transfer,
			relationValue,
			notifications,
			dispatcherContext.additionalContext,
		]
	);

	// ── Button group layout logic ──
	// When a value IS set:
	//   Primary: View only
	//   Dropdown: Set, Unset, Create, Delete (filtered by guards)
	// When NO value is set:
	//   Primary: Set + Create (if model allows)
	//   No dropdown
	const hasValue = !!relationValue;

	const primaryButtons = useMemo(() => {
		if (hasValue) {
			// Value present → View is the primary action
			return viewButton ? [viewButton] : [];
		}
		// No value → Set and Create are primary actions
		const result: typeof visibleButtons = [];
		if (setButton) result.push(setButton);
		if (createButton) result.push(createButton);
		return result;
	}, [hasValue, viewButton, setButton, createButton]);

	const dropdownButtons = useMemo(() => {
		if (!hasValue) return [];
		// Value present → everything except View goes in the dropdown, filtered by guards
		const result: typeof visibleButtons = [];
		if (setButton) result.push(setButton);
		if (unsetButton && relationValue) result.push(unsetButton);
		if (createButton) result.push(createButton);
		if (deleteButton && relationValue?.__deleteable === true) result.push(deleteButton);
		return result;
	}, [hasValue, setButton, unsetButton, createButton, deleteButton, relationValue]);

	// Dropdown menu state (Popper-based, like original template)
	const [dropdownOpen, setDropdownOpen] = useState(false);
	const anchorRef = useRef<HTMLDivElement>(null);

	const handleDropdownToggle = useCallback(() => {
		setDropdownOpen((prev) => !prev);
	}, []);

	const handleDropdownClose = useCallback((event: Event | React.SyntheticEvent) => {
		if (anchorRef.current?.contains(event.target as HTMLElement)) {
			return;
		}
		setDropdownOpen(false);
	}, []);

	// Helper: dispatch a button's action
	const dispatchButtonAction = useCallback(
		async (button: (typeof visibleButtons)[number], contextOverrides?: Record<string, unknown>) => {
			const action = page ? findActionByDefinition(page, button.actionDefinition) : undefined;
			if (!action) return;

			try {
				const handlerHook = getActionHandler(action as Action);
				if (!handlerHook) {
					throw new Error(`No handler for action type: ${button.actionDefinition?.["@type"]}`);
				}
				const handlerFn = handlerHook(action as Action);
				const rowContext = {
					navigation,
					data: dataStore,
					registry: application,
					api,
					transfer: contextOverrides?.transfer ?? relationValue ?? transfer,
					notifications,
					...dispatcherContext.additionalContext,
					...contextOverrides,
				};
				await handlerFn(rowContext as any);
			} catch (error) {
				console.error("Button action failed:", error);
			}
		},
		[
			page,
			navigation,
			dataStore,
			application,
			api,
			transfer,
			relationValue,
			notifications,
			dispatcherContext.additionalContext,
		]
	);

	// Handle View button
	const handleView = useCallback(() => {
		if (viewButton && relationValue) {
			dispatchButtonAction(viewButton, { transfer: relationValue });
		}
	}, [viewButton, relationValue, dispatchButtonAction]);

	// Handle Set button → open selector dialog
	const handleOpenSelector = useCallback(() => {
		setSelectorOpen(true);
	}, []);

	// Handle Unset button
	const handleUnset = useCallback(async () => {
		if (unsetButton) {
			const confirmed = await confirm(getDestructiveConfirmationOptions("UnsetActionDefinition"));
			if (!confirmed) return;
			if (element.isEager && pageContext?.transferId) {
				// Eager: just clear the DataStore field, no REST call
				dataStore.updateTransferField(pageContext.transferId, element.relationName, null);
			} else {
				// Lazy: dispatch the unset REST action
				dispatchButtonAction(unsetButton);
			}
		}
	}, [
		unsetButton,
		dispatchButtonAction,
		element.isEager,
		element.relationName,
		dataStore,
		pageContext?.transferId,
		confirm,
	]);

	// Handle Create button
	const handleCreate = useCallback(() => {
		if (createButton) {
			dispatchButtonAction(createButton);
		}
	}, [createButton, dispatchButtonAction]);

	// Handle Delete button
	const handleDelete = useCallback(async () => {
		if (deleteButton && relationValue) {
			const confirmed = await confirm(getDestructiveConfirmationOptions("RowDeleteActionDefinition"));
			if (!confirmed) return;
			dispatchButtonAction(deleteButton, { transfer: relationValue });
		}
	}, [deleteButton, relationValue, dispatchButtonAction, confirm]);

	// Map button to its click handler
	const getButtonHandler = useCallback(
		(button: (typeof visibleButtons)[number]) => {
			const actionType = button.actionDefinition?.["@type"];
			switch (actionType) {
				case "OpenPageActionDefinition":
				case "RowOpenPageActionDefinition":
					return handleView;
				case "OpenSetSelectorActionDefinition":
					return handleOpenSelector;
				case "UnsetActionDefinition":
					return handleUnset;
				case "OpenCreateFormActionDefinition":
					return handleCreate;
				case "RowDeleteActionDefinition":
					return handleDelete;
				default:
					return () => {};
			}
		},
		[handleView, handleOpenSelector, handleUnset, handleCreate, handleDelete]
	);

	// Map button to its default label
	const _getButtonDefaultLabel = useCallback((button: (typeof visibleButtons)[number]) => {
		const actionType = button.actionDefinition?.["@type"];
		switch (actionType) {
			case "OpenPageActionDefinition":
			case "RowOpenPageActionDefinition":
				return "View";
			case "OpenSetSelectorActionDefinition":
				return "Set";
			case "UnsetActionDefinition":
				return "Unset";
			case "OpenCreateFormActionDefinition":
				return "Create";
			case "RowDeleteActionDefinition":
				return "Delete";
			default:
				return "Action";
		}
	}, []);

	// Handle selector dialog confirm
	// The LinkSelectorDialog is an inline dialog (not the page-based navigation
	// dialog). After the user picks an item we must call api.setRelation()
	// directly (lazy) or update the DataStore field (eager).  We must NOT
	// dispatch the setButton's action because that is
	// OpenSetSelectorActionDefinition which would re-open the selector.
	const handleSelectorConfirm = useCallback(
		async (selected: OptionRecord) => {
			setSelectorOpen(false);

			if (!relationType) return;

			try {
				if (element.isEager && pageContext?.transferId) {
					// Eager: just update the DataStore field, no REST call
					dataStore.updateTransferField(pageContext.transferId, element.relationName, selected);
				} else if (api && transfer) {
					// Lazy: call api.setRelation() to persist the selection
					await api.setRelation(
						relationType,
						transfer as import("@judo/actions").TransferStored,
						selected as import("@judo/actions").TransferStored
					);
				}
			} catch (error) {
				console.error("Failed to set from selector:", error);
			}
		},
		[relationType, api, transfer, element.isEager, element.relationName, dataStore, pageContext?.transferId]
	);

	// Handle selector dialog cancel
	const handleSelectorCancel = useCallback(() => {
		setSelectorOpen(false);
	}, []);

	// Check option equality by __signedIdentifier or __identifier
	// Prefer __identifier (stable UUID) over __signedIdentifier (JWT that may
	// change between DataStore value and fresh range response), consistent with
	// the mergedOptions dedup logic.
	const isOptionEqualToValue = useCallback((option: OptionRecord, value: OptionRecord) => {
		if (option.__identifier && value.__identifier) {
			return option.__identifier === value.__identifier;
		}
		if (option.__signedIdentifier && value.__signedIdentifier) {
			return option.__signedIdentifier === value.__signedIdentifier;
		}
		return option === value;
	}, []);

	const hasActionButtons = primaryButtons.length > 0 || dropdownButtons.length > 0;

	return (
		<Box data-testid={testId} sx={{ display: "flex", alignItems: "stretch" }}>
			<Box sx={{ flexGrow: 1 }}>
				<Autocomplete
					value={relationValue}
					onChange={handleChange}
					onInputChange={handleInputChange}
					onOpen={handleOpen}
					options={mergedOptions}
					getOptionLabel={getOptionLabel}
					filterOptions={(x) => x}
					isOptionEqualToValue={isOptionEqualToValue}
					loading={autocompleteLoading}
					disabled={isReadOnly}
					freeSolo={false}
					data-testid={`${testId}::autocomplete`}
					{...(hasActionButtons && {
						sx: {
							"& .MuiOutlinedInput-root": {
								borderTopRightRadius: 0,
								borderBottomRightRadius: 0,
							},
						},
					})}
					renderOption={({ key: _muiKey, ...optionProps }, option) => {
						const key = String(option.__identifier ?? option.__signedIdentifier ?? getOptionLabel(option));
						return (
							<li {...optionProps} key={key}>
								{getOptionLabel(option)}
							</li>
						);
					}}
					renderInput={({ inputProps: autoHtmlInputProps, InputProps: autoInputProps, ...restParams }) => (
						<TextField
							{...restParams}
							label={label}
							required={!isOptional}
							fullWidth
							slotProps={{
								input: {
									...autoInputProps,
									startAdornment: startAdornment ? (
										<>
											{startAdornment}
											{autoInputProps.startAdornment}
										</>
									) : (
										autoInputProps.startAdornment
									),
								},
								inputLabel: { shrink: true },
								htmlInput: {
									...autoHtmlInputProps,
									"data-testid": `${testId}::autocomplete::input`,
								},
							}}
						/>
					)}
				/>
			</Box>

			{hasActionButtons && (
				<Box
					ref={anchorRef}
					data-testid={`${testId}::actions`}
					aria-label="link actions"
					role="group"
					sx={(theme) => {
						const radius = theme.shape.borderRadius;
						// Match MuiOutlinedInput.notchedOutline borderColor from judo-theme-factory
						const borderColor = theme.palette.mode === "light" ? "rgba(0, 0, 0, 0.15)" : "rgba(255, 255, 255, 0.15)";
						return {
							display: "flex",
							alignItems: "center",
							marginTop: densityMargins.top,
							marginBottom: densityMargins.bottom,
							border: `1px solid ${borderColor}`,
							borderLeft: "none",
							borderRadius: `0 ${radius}px ${radius}px 0`,
							"&:hover": {
								borderColor: theme.palette.text.primary,
							},
						};
					}}
				>
					{primaryButtons.map((button) => (
						<LinkActionButton key={button["xmi:id"]} button={button} onClick={getButtonHandler(button)} />
					))}
					{dropdownButtons.length > 0 && (
						<IconButton
							size="small"
							onClick={handleDropdownToggle}
							data-testid={`${testId}::actions::dropdown`}
							aria-label="more actions"
							sx={{ borderRadius: 0, px: 0.5 }}
						>
							<ArrowDropDownIcon fontSize="small" />
						</IconButton>
					)}
				</Box>
			)}

			<Popper
				open={dropdownOpen}
				anchorEl={anchorRef.current}
				transition
				placement="bottom-end"
				sx={{ zIndex: 1 }}
				data-testid={`${testId}::actions::menu`}
			>
				{({ TransitionProps, placement }) => (
					<Grow
						{...TransitionProps}
						style={{ transformOrigin: placement === "bottom-end" ? "right top" : "right bottom" }}
					>
						<Paper>
							<ClickAwayListener onClickAway={handleDropdownClose}>
								<MenuList autoFocusItem={dropdownOpen}>
									{dropdownButtons.map((button) => (
										<LinkDropdownItem
											key={button["xmi:id"]}
											button={button}
											onClick={() => {
												setDropdownOpen(false);
												getButtonHandler(button)();
											}}
										/>
									))}
								</MenuList>
							</ClickAwayListener>
						</Paper>
					</Grow>
				)}
			</Popper>

			{selectorOpen && relationType && (
				<LinkSelectorDialog
					open={selectorOpen}
					element={element}
					relationType={relationType}
					api={api}
					transfer={transfer}
					onConfirm={handleSelectorConfirm}
					onCancel={handleSelectorCancel}
				/>
			)}
		</Box>
	);
}

/** Link action button rendered as a compact icon button matching Autocomplete indicator size. */
function LinkActionButton({ button, onClick }: { button: ButtonElement; onClick: () => void }) {
	const { label } = useModelLabel(button);
	return (
		<Tooltip title={label}>
			<IconButton
				size="small"
				onClick={onClick}
				data-testid={getButtonTestId(button)}
				aria-label={label}
				sx={{ borderRadius: 0, px: 1 }}
			>
				{button.icon ? <IconRenderer icon={button.icon} /> : <span style={{ fontSize: "0.875rem" }}>{label}</span>}
			</IconButton>
		</Tooltip>
	);
}

/** Link dropdown menu item with translated label. */
function LinkDropdownItem({ button, onClick }: { button: ButtonElement; onClick: () => void }) {
	const { label } = useModelLabel(button);
	return (
		<MenuItem onClick={onClick} data-testid={getButtonTestId(button)}>
			{button.icon && <IconRenderer icon={button.icon} />}
			<span style={{ marginLeft: button.icon ? 8 : 0 }}>{label}</span>
		</MenuItem>
	);
}
